import createClient from "openapi-fetch";
import type { components, paths } from "./api"; // generated by openapi-typescript

import * as L from "leaflet";

import Chart from "chart.js/auto";
import "chartjs-adapter-luxon";

import { render } from "solid-js/web";
import { mapState, setMapState, WeatherMapState } from "./state";
import {
  Component,
  createEffect,
  createMemo,
  For,
  onCleanup,
  onMount,
  Show,
  VoidComponent,
} from "solid-js";
import { createStore, unwrap } from "solid-js/store";

import { DateTime } from "luxon";
import { LatLngBounds, LatLngTuple } from "leaflet";
import {
  getCelcius,
  getcmHeightInterval,
  getmmHeightInterval,
} from "./helpers";

const client = createClient<paths>({ baseUrl: "/" });

export function initMap() {
  setInterval(() => {
    if (document.visibilityState != "visible") return;
    let data = {
      sidebarOpen: mapState.sidebarOpen,
      onlyDots: mapState.onlyDots,
      hideDots: mapState.hideDots,
      withSunSensor: mapState.withSunSensor,
      withRainSensor: mapState.withRainSensor,
      withImages: mapState.withImages,
      rainRadar: mapState.rainRadar,
      graphForLastXMinutes: mapState.graphForLastXMinutes,
      currentMapCenter: mapState.currentMapCenter,
      currentMapZoom: mapState.currentMapZoom,
    };
    window.localStorage.setItem("mapstate", JSON.stringify(data));
  }, 1000);

  let mapstate = window.localStorage.getItem("mapstate");
  if (mapstate) {
    let mapstateObj = JSON.parse(mapstate);
    if (mapstateObj) {
      setMapState(JSON.parse(mapstate));
    }
  }

  let mapSidebar = document.createElement("div");
  mapSidebar.classList.add("mapsidebar");
  document.querySelector("#weathermap").append(mapSidebar);

  let mapEl = document.createElement("div");
  mapEl.id = "weathermap-leaflet";
  document.querySelector("#weathermap").append(mapEl);

  render(() => <MapSidebarControl />, mapSidebar);

  let radarIntervalRedraw: NodeJS.Timeout;
  onMount(() => {
    radarIntervalRedraw = setInterval(() => {
      updateRainRadarStep();
    }, 1000);
  });

  onCleanup(() => {
    clearInterval(radarIntervalRedraw);
  });

  let mapCenter =
    mapState.currentMapCenter[0] != 0
      ? unwrap(mapState.currentMapCenter)
      : [46.242838, 14.349971];

  let map = L.map("weathermap-leaflet", {
    // scrollWheelZoom: false,
    // touchZoom: false,
    // zoomControl: false,
    // doubleClickZoom: false,
    // boxZoom: false
  }).setView(mapCenter as LatLngTuple, mapState.currentMapZoom);

  // map.scrollWheelZoom.disable();
  // map.touchZoom.disable();

  // const el = map.getContainer();

  // ['gesturestart', 'gesturechange', 'gestureend'].forEach(evt => {
  //     el.addEventListener(evt, e => e.preventDefault());
  // });

  setMapState("map", map);

  // map.getContainer().addEventListener('wheel', (e) => {
  //     e.preventDefault();

  //     const delta = e.deltaY;
  //     const zoom = map.getZoom();

  //     if (delta < 0) {
  //         map.setZoom(zoom + 1); // zoom in
  //     } else {
  //         map.setZoom(zoom - 1); // zoom out
  //     }
  // }, { passive: false });

  L.tileLayer("http://{s}.tile.osm.org/{z}/{x}/{y}.png", {}).addTo(map);

  // (L.control as any).mapsidebarcontrol({ position: 'topleft' }).addTo(map);

  map.on("moveend", () => {
    // console.log(map.getCenter(), map.getZoom())
    setMapState({
      currentMapCenter: [map.getCenter().lat, map.getCenter().lng],
      currentMapZoom: map.getZoom(),
    });
  });

  updateLatestMarkers();

  updateRainRadar();
}

const updateRainRadarStep = async (forceSliderReadout = false) => {
  if (!mapState.rainRadar) {
    return;
  }

  if (forceSliderReadout) {
  } else if (mapState.radarData.forceRefresh) {
    setMapState("radarData", "forceRefresh", false);
  } else if (mapState.radarData.paused) {
    return;
  }

  if (forceSliderReadout) {
    let calcIndex = Math.round(
      mapState.radarData.sliderValue *
        (mapState.radarData.data.items.length - 1),
    );
    if (calcIndex == mapState.radarData.currentIdx) {
      return;
    }
    setMapState("radarData", "currentIdx", calcIndex);
  } else {
    let nextIndex = mapState.radarData.currentIdx + 1;
    if (nextIndex >= mapState.radarData.data.items.length) {
      if (
        DateTime.now().diff(mapState.radarData.last_step_at, "seconds")
          .seconds > 3
      ) {
        setMapState("radarData", "currentIdx", 0);
        setMapState("radarData", "last_step_at", DateTime.now());
      }
    } else {
      setMapState("radarData", "currentIdx", nextIndex);
      setMapState("radarData", "last_step_at", DateTime.now());
    }

    setMapState(
      "radarData",
      "sliderValue",
      (mapState.radarData.currentIdx + 1) /
        (mapState.radarData.data.items.length * 1.0),
    );
  }

  let canvas = mapState.overlayRadarDebug
    ? mapState.overlayRadarDebug
    : document.createElement("canvas");

  const ctx = canvas.getContext("2d");

  let rdr = mapState.radarData.data.items[mapState.radarData.currentIdx].data;

  canvas.width = rdr.width;
  canvas.height = rdr.height;

  if (typeof rdr.data == "object" && "RLE" in rdr.data) {
    let pixidx = 0;
    const imageData = ctx.createImageData(rdr.width, rdr.height);

    for (let d of rdr.data["RLE"]) {
      if ("None" in d) {
        pixidx += d.None;
      } else if ("ValuesF32" in d) {
        for (let v of d.ValuesF32) {
          const p = pixidx * 4;
          const colour = getRainRadarPixelColour(rdr.unit, v);
          // RGBA
          imageData.data[p + 0] = colour[0] * 255;
          imageData.data[p + 1] = colour[1] * 255;
          imageData.data[p + 2] = colour[2] * 255;
          imageData.data[p + 3] = 200;

          pixidx += 1;
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);

    const bounds = boundsFromOrigin(
      rdr.origin.lat,
      rdr.origin.lon,
      rdr.width,
      rdr.height,
    );
    if (mapState.mapRadarOverlay) {
      mapState.mapRadarOverlay.remove();
    }

    let canvasRadarOverlay = L.imageOverlay(canvas.toDataURL(), bounds, {
      opacity: 1,
    }).addTo(mapState.map);

    setMapState("mapRadarOverlay", canvasRadarOverlay);
  }
};

const updateRainRadar = async () => {
  if (!mapState.rainRadar) {
    if (mapState.mapRadarOverlay) {
      mapState.mapRadarOverlay.remove();
    }
    return;
  }
  setMapState("radarData", "loaded", false);

  let radar_req = await client.GET("/api/radar/find", {
    params: {
      query: {
        last_x_minutes: 60,
        radar: "arso-rr",
        // radar: "arso-zm",
      },
    },
  });

  if (radar_req.response.ok && radar_req.data.items.length >= 1) {
    setMapState("radarData", "currentIdx", radar_req.data.items.length - 1);
    // sort descending
    radar_req.data.items.sort((a, b) => a.id - b.id);
    setMapState("radarData", "data", radar_req.data);
    setMapState("radarData", "loaded", true);
    setMapState(
      "radarData",
      "next_step_at",
      DateTime.now().plus({ seconds: 3 }),
    );

    updateRainRadarStep();
  }
};

const getRainRadarPixelColour = (unit: String, v: number) => {
  if ((unit = "DBRH")) {
    // -6 .. 20
    const ratio = clampNumber((v + 6.0) / 26.0, 0.0, 1.0);
    return hsl2rgb(200.0 + 159.0 * ratio, 0.5, 0.5);
  } else if ((unit = "DBZ")) {
    // -6 .. 20
    const ratio = clampNumber((v - 15.0) / 55.0, 0.0, 1.0);
    return hsl2rgb(200.0 + 159.0 * ratio, 0.5, 0.5);
  }
  [255, 0, 0];
};

function clampNumber(nb: number, min: number, max: number) {
  return Math.min(Math.max(nb, min), max);
}

// https://stackoverflow.com/questions/2353211/hsl-to-rgb-color-conversion
// https://www.w3schools.com/colors/colors_hsl.asp
// hsl2rgb(0, 1.0, 0.5) = [1.0, 0.0, 0.0] = red
function hsl2rgb(h, s, l) {
  let a = s * Math.min(l, 1 - l);
  let f = (n, k = (n + h / 30) % 12) =>
    l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
  return [f(0), f(8), f(4)];
}

const KM_PER_DEG_LAT = 111.32;

function boundsFromOrigin(lat0, lon0, widthKm, heightKm) {
  let halfHeightDeg = heightKm / KM_PER_DEG_LAT / 2;

  const north = lat0 + heightKm / KM_PER_DEG_LAT;

  const kmPerDegLon = KM_PER_DEG_LAT * Math.cos((lat0 * Math.PI) / 180);

  const east = lon0 + widthKm / kmPerDegLon;

  let halfWidthDeg = widthKm / kmPerDegLon / 2;

  return L.latLngBounds(
    [lat0 - halfHeightDeg, lon0 - halfWidthDeg], // SW
    [north - halfHeightDeg, east - halfWidthDeg], // NE
  );
}

const updateLatestMarkers = async () => {
  for (let m of mapState.markers) {
    m.remove();
  }

  if (mapState.rainRadar) {
    setMapState("markers", []);
    return;
  }

  let dlocations = await client.GET("/api/locations");
  if (dlocations.data) {
    setMapState("locations", dlocations.data.list);
  }

  const d = await client.GET("/api/latest", {
    params: {},
  });

  if (d.data) {
    let markers = [];
    for (let xx of d.data?.list) {
      if (!xx.temperature) continue;
      if (!xx.location_xy) continue;

      let emptyDiv = L.divIcon({
        html: "",
        iconSize: undefined,
        className: undefined,
      });

      let marker = L.marker(
        [xx.location_xy?.LatLon[0], xx.location_xy?.LatLon[1]],
        { icon: emptyDiv },
      ).addTo(mapState.map);
      let markerEl = marker.getElement();
      if (markerEl) {
        // mount/render icon
        render(() => <WeatherIcon data={xx} />, markerEl);
      }
      markers.push(marker);
    }
    setMapState("markers", markers);
  }
};

const MapSidebarControl: VoidComponent = () => {
  // console.log(mapState.graphForLastXMinutes)
  return (
    <>
      {/*
        <div class="mapsidebar__iconopen" onclick={() => {
            setMapState({ sidebarOpen: !mapState.sidebarOpen });
        }}>‹</div> */}
      <LocationOverlay />
      <a class="mapsidebar__pagelogo" href="/">
        weather.smmx.dev
      </a>
      <div class="mapsidebar__content">
        <div
          class="pointer"
          onclick={() => {
            updateLatestMarkers();
          }}
        >
          Refresh map data
        </div>
        <div class="mapsidebar__divider" />
        <div class="mapsidebar__filters">
          <div>
            <label>
              <input
                type="checkbox"
                checked={mapState.rainRadar}
                onChange={(e) => {
                  setMapState("rainRadar", e.currentTarget.checked);
                  setMapState("radarData", "forceRefresh", true);
                  updateLatestMarkers();
                  updateRainRadar();
                  updateRainRadarStep();
                }}
              />
              Rain Radar (last hour)
            </label>
          </div>
          <Show when={mapState.rainRadar}>
            <RainRadarControl />
          </Show>
          <div>
            <label>
              <input
                type="checkbox"
                checked={mapState.hideDots}
                onChange={(e) =>
                  setMapState("hideDots", e.currentTarget.checked)
                }
              />
              Hide overlays
            </label>
          </div>
          <div>
            <label>
              <input
                type="checkbox"
                disabled={mapState.hideDots}
                checked={mapState.onlyDots}
                onChange={(e) =>
                  setMapState("onlyDots", e.currentTarget.checked)
                }
              />
              Only overlays
            </label>
          </div>
          <div>
            <label>
              <input
                type="checkbox"
                checked={mapState.withRainSensor}
                onChange={(e) =>
                  setMapState("withRainSensor", e.currentTarget.checked)
                }
              />
              With rain sensor
            </label>
          </div>
          <div>
            <label>
              <input
                type="checkbox"
                checked={mapState.withSunSensor}
                onChange={(e) =>
                  setMapState("withSunSensor", e.currentTarget.checked)
                }
              />
              With sun sensor
            </label>
          </div>
          <div>
            <label>
              <input
                type="checkbox"
                checked={mapState.withImages}
                onChange={(e) =>
                  setMapState("withImages", e.currentTarget.checked)
                }
              />
              With images
            </label>
          </div>
          {/*<div>
            <label>
              Show graph for
              <input
                type="number"
                step={15}
                style={{
                  width: "60px",
                  margin: "0 5px",
                }}
                min={1}
                max={60 * 24 * 2}
                value={mapState.graphForLastXMinutes}
                onChange={(e) =>
                  setMapState(
                    "graphForLastXMinutes",
                    Number(e.currentTarget.value),
                  )
                }
              />
              minutes
            </label>
          </div>*/}
        </div>
      </div>
      <div class="mapsidebar__spacer"></div>
      <div class="mapsidebar__links">
        <a href="/">homepage</a>
        <a href="https://smmx.dev" target="_blank">
          smmx.dev
        </a>
      </div>
      {/*<canvas
        id="canvas-dbg-radar"
        ref={(el) => {
          setMapState("overlayRadarDebug", el);
        }}
      ></canvas>*/}
    </>
  );
};

const RainRadarControl: VoidComponent = () => {
  return (
    <div class="radar-controls">
      <div class="radar-controls__control">
        <Show when={mapState.radarData.loaded}>
          Displaying {mapState.radarData.currentIdx + 1} /{" "}
          {mapState.radarData.data.items.length}
        </Show>
      </div>
      <div class="radar-controls__control">
        <input
          type="checkbox"
          checked={!mapState.radarData.paused}
          onchange={() => {
            setMapState("radarData", "paused", !mapState.radarData.paused);
          }}
        />{" "}
        autoplay
      </div>
      <div class="radar-controls__control">
        <input
          type="range"
          min={0}
          max={1}
          value={mapState.radarData.sliderValue}
          step={0.00001}
          onpointerdown={() => {
            setMapState("radarData", "paused", true);
          }}
          oninput={(e) => {
            setMapState("radarData", "sliderValue", e.target.value);
            updateRainRadarStep(true);
          }}
        />
      </div>
    </div>
  );
};

const WeatherIcon: Component<{
  data: components["schemas"]["HttpResponseLatestItem"];
}> = (props) => {
  const displayWhite = createMemo(() => {
    if (mapState.onlyDots) return false;
    if (mapState.withRainSensor && !props.data.rain) return false;
    if (mapState.withSunSensor && !props.data.sun) return false;
    if (mapState.withImages && props.data.pictures.length == 0) return false;
    return true;
  });

  const displayOverlay = createMemo(() => {
    if (mapState.hideDots) return false;
    if (mapState.withRainSensor && !props.data.rain) return false;
    if (mapState.withSunSensor && !props.data.sun) return false;
    if (mapState.withImages && props.data.pictures.length == 0) return false;
    return true;
  });

  let celcius = getCelcius(props.data.temperature);

  return (
    <div
      class="map-icon-info"
      onclick={() => {
        setMapState({
          overlayOpen: true,
          overlayLoaded: false,
          overlayLocation: props.data.id_location,
          overlayData: props.data,
        });
      }}
    >
      <Show when={displayWhite()}>
        <div
          classList={{
            "map-icon-info__data": true,
            "map-icon-info__temperature": true,
            "map-icon-info__positive": celcius >= 0,
            "map-icon-info__negative": celcius < 0,
          }}
        >
          <Show when={celcius < 0}>
            {(_) => {
              return (
                <>
                  <span class="minustemp">-</span>
                  {Math.round(Math.abs(celcius))}°
                </>
              );
            }}
          </Show>
          <Show when={celcius >= 0}>
            {(_) => {
              return <>{Math.round(celcius)}°</>;
            }}
          </Show>
        </div>
      </Show>
      <Show when={displayOverlay() && props.data.rain}>
        {(_) => {
          let height = getmmHeightInterval(props.data.rain.ammount_interval);
          if (height && height > 0) {
            return <div class="raining" style={{ "--size": height }}></div>;
          }
          return <></>;
        }}
      </Show>
      <Show when={displayOverlay() && props.data.sun}>
        {(_) => {
          let sunir = props.data.sun.irradiance;
          if (sunir > 10) {
            return <div class="sun" style={{ "--size": sunir }}></div>;
          }
          return <></>;
        }}
      </Show>
    </div>
  );
};

const LocationOverlay: Component<{}> = (props) => {
  const closeOverlay = () => {
    console.log("close");
    setMapState({ overlayOpen: false });
  };

  let canvas_temperature: HTMLCanvasElement;
  let canvas_pressure: HTMLCanvasElement;

  const [localOverlayState, setLocalOverlayState] = createStore({
    hasTemperatureData: false,
    hasBarometricData: false,
  });

  createEffect(async () => {
    if (mapState.overlayOpen == false || !mapState.overlayLocation) {
      return;
    }

    setMapState("overlayDataLatest", undefined);

    try {
      const dlatest = await client.GET("/api/latest", {
        params: {
          query: {
            id_location: mapState.overlayLocation,
          },
        },
      });
      if (dlatest.response.ok && dlatest.data.list.length > 0) {
        setMapState("overlayDataLatest", dlatest.data.list[0]);
      } else {
        setMapState("overlayDataLatest", undefined);
      }
    } catch {}

    const d = await client.GET("/api/find", {
      params: {
        query: {
          location: mapState.overlayLocation,
          minutes: mapState.graphForLastXMinutes,
        },
      },
    });

    if (!d.data || d.data.list.length == 0) {
      return;
    }

    d.data.list.sort((a, b) => {
      return a.dt - b.dt;
    });

    setLocalOverlayState("hasTemperatureData", true);
    new Chart(canvas_temperature, {
      type: "line",

      options: {
        animation: false,
        scales: {
          x: {
            type: "time",
            time: {
              // Luxon format string
              tooltipFormat: "hh:mm",
            },
            title: {
              display: true,
              text: "Date",
            },
          },
        },
      },
      data: {
        labels: d.data.list.map((_x) => {
          return DateTime.fromMillis(_x.dt);
        }),
        datasets: [
          {
            label: "temperature",
            data: d.data.list.map((x) => {
              return getCelcius(x.temperature);
            }),
          },
        ],
      },
    });

    if (d.data.list[0].barometric_pressure) {
      setLocalOverlayState("hasBarometricData", true);
      new Chart(canvas_pressure, {
        type: "line",
        options: {
          animation: false,
          scales: {
            x: {
              type: "time",
              time: {
                // Luxon format string
                tooltipFormat: "hh:mm",
              },
              title: {
                display: true,
                text: "Date",
              },
            },
          },
        },
        data: {
          labels: d.data.list.map((_x) => {
            return DateTime.fromMillis(_x.dt);
          }),
          datasets: [
            {
              label: "pressure",
              data: d.data.list.map((x) => {
                return x.barometric_pressure;
              }),
            },
          ],
        },
      });
    } else {
      setLocalOverlayState("hasBarometricData", false);
    }
  });

  let locName = createMemo(() => {
    let l = mapState.locations.find(
      (x) => x.id_location == mapState.overlayLocation,
    );
    if (l) {
      return l.location;
    } else {
      return "unknown location";
    }
  });

  return (
    <>
      <Show when={mapState.overlayOpen}>
        <div
          class="weatherinfo-detailed"
          onclick={(e) => {
            if (e.target == e.currentTarget) closeOverlay();
          }}
        >
          <div class="weatherinfo-detailed__contentouter">
            <div class="weatherinfo-detailed__content">
              <div class="weatherinfo-detailed__header">
                <div class="weatherinfo-detailed__header1">
                  <div class="title">{locName()}</div>
                  <Show when={mapState.overlayDataLatest != undefined}>
                    {(x) => {
                      return (
                        <div class="table-wrapper">
                          <table class="table mt-3">
                            <tbody>
                              <Show when={mapState.overlayDataLatest.altitude}>
                                <tr>
                                  <td class="nowrap" style={{ width: 0 }}>
                                    Altitude
                                  </td>
                                  <td>
                                    {mapState.overlayDataLatest.altitude.Meters}{" "}
                                    m
                                  </td>
                                </tr>
                              </Show>

                              <Show
                                when={
                                  mapState.overlayDataLatest.barometric_pressure
                                }
                              >
                                <tr>
                                  <td class="nowrap">Barometric Pressure</td>
                                  <td>
                                    {
                                      mapState.overlayDataLatest
                                        .barometric_pressure
                                    }{" "}
                                    hPa
                                  </td>
                                </tr>
                              </Show>

                              <Show when={mapState.overlayDataLatest.humidity}>
                                <tr>
                                  <td class="nowrap">Humidity</td>
                                  <td>
                                    {
                                      mapState.overlayDataLatest.humidity
                                        .Relative
                                    }
                                    %
                                  </td>
                                </tr>
                              </Show>

                              <Show when={mapState.overlayDataLatest.rain}>
                                <tr>
                                  <td class="nowrap">Rain last hour</td>
                                  <td>
                                    {getmmHeightInterval(
                                      mapState.overlayDataLatest.rain
                                        .ammount_1h,
                                    )}{" "}
                                    mm
                                  </td>
                                </tr>
                              </Show>

                              <Show when={mapState.overlayDataLatest.snow}>
                                <tr>
                                  <td class="nowrap">Snow last hour</td>
                                  <td>
                                    {getcmHeightInterval(
                                      mapState.overlayDataLatest.snow.current,
                                    )}{" "}
                                    cm
                                  </td>
                                </tr>
                              </Show>

                              <Show when={mapState.overlayDataLatest.sun}>
                                <tr>
                                  <td class="nowrap">Sun irradiance</td>
                                  <td>
                                    {mapState.overlayDataLatest.sun.irradiance}{" "}
                                    W/m²
                                  </td>
                                </tr>
                              </Show>

                              <Show
                                when={mapState.overlayDataLatest.temperature}
                              >
                                <tr>
                                  <td class="nowrap">Temperature</td>
                                  <td>
                                    {getCelcius(
                                      mapState.overlayDataLatest.temperature,
                                    )}{" "}
                                    °C
                                  </td>
                                </tr>
                              </Show>

                              <Show when={mapState.overlayDataLatest.wind}>
                                <tr>
                                  <td class="nowrap">Average Wind</td>
                                  <td>
                                    {
                                      mapState.overlayDataLatest.wind.direction
                                        .Degrees
                                    }
                                    °{" "}
                                    {mapState.overlayDataLatest.wind.speed.Kph}{" "}
                                    km/h
                                  </td>
                                </tr>
                              </Show>
                            </tbody>
                          </table>
                        </div>
                      );
                    }}
                  </Show>
                  <a
                    href={`/location/${mapState.overlayLocation}`}
                    class="btn mt-2"
                  >
                    show more
                  </a>
                </div>

                <div class="weatherinfo-detailed__close">
                  <div class="mt-2" onclick={() => closeOverlay()}>
                    ✕ close
                  </div>
                </div>
              </div>

              <Show when={mapState.overlayData.pictures.length}>
                <div class="weatherinfo-detailed__images">
                  <For each={mapState.overlayData.pictures}>
                    {(p) => {
                      return (
                        <div class="weatherinfo-detailed__image">
                          <img src={p.url} />
                        </div>
                      );
                    }}
                  </For>
                </div>
              </Show>

              <div
                class="mt-2"
                classList={{
                  hidden: !localOverlayState.hasTemperatureData,
                }}
              >
                Temperature chart
                <canvas ref={canvas_temperature}></canvas>
              </div>

              <div
                class="mt-2"
                classList={{
                  hidden: !localOverlayState.hasBarometricData,
                }}
              >
                Pressure chart
                <canvas ref={canvas_pressure}></canvas>
              </div>
            </div>
          </div>
        </div>
      </Show>
    </>
  );
};

// (L.Control as any).MapSidebarControl = L.Control.extend({
//     onAdd: function (_map: L.Map) {
//         let el = L.DomUtil.create('div');
//         // el.innerHTML = 'asdf';
//         // mount render map sidemenu
//         // m.mount(el, MapSidebarControl);
//         render(() => <MapSidebarControl />, el)
//         return el;
//     },

//     onRemove: function (_map: L.Map) {
//         // Nothing to do here
//     }
// });

// (L.control as any).mapsidebarcontrol = function (opts: any) {
//     return new (L.Control as any).MapSidebarControl(opts);
// }

function valueToRGB(n) {
  if (n === 0) return "rgb(0,0,0)";

  if (n < 0) {
    const t = Math.min(Math.abs(n) / 20, 1); // 0 → 1
    const b = Math.round(255 * t);
    return `rgb(0,0,${b})`; // toward #00f
  }

  const t = Math.min(n / 40, 1); // 0 → 1
  const r = Math.round(255 * t);
  return `rgb(${r},0,0)`; // toward #f00
}
